/**
 * Consent gate for the storefront tracking (Webgains + Google Tag Manager).
 *
 * Tracking starts only after a recognized consent decision. The Shopify
 * Customer-Privacy-API method window.Shopify.customerPrivacy.getTrackingConsent()
 * returns one of:
 *   "yes"            - marketing and analytics accepted  -> tracking allowed
 *   "no"             - explicitly declined              -> never track
 *   "no_interaction" - consent data present, no decision -> do not track yet
 *   ""               - no consent data at all            -> do not track yet
 *
 * The previous implementation read the value once 2 s after page load and
 * used a truthy check, which treated "no_interaction" as consent and loaded
 * the tags without a decision (issue !13).
 *
 * The consent decision arrives asynchronously: Consentmo registers it with the
 * Customer-Privacy-API, which then dispatches the document event
 * "visitorConsentCollected". This gate re-evaluates on that event, so tracking
 * starts as soon as a "yes" decision is present. Visitors who already consented
 * are covered by the immediate evaluation (the decision is stored).
 *
 * The timeout is a fallback only for the case where consent data is
 * definitively not available (no decision has arrived after a while): tracking
 * is not started, and a console entry documents the state. It never starts
 * tracking on its own - only an explicit "yes" decision does.
 *
 * Loaded with "defer" in layout/theme.liquid, i.e. after the DOM is parsed and
 * the Customer-Privacy-API (injected via content_for_header in <head>) has run.
 */
(() => {
    const CONSENT_WAIT_TIMEOUT_MS = 10000;

    let trackingStarted = false;
    let waitingStarted = false;

    function startTracking() {
        if (trackingStarted) {
            return;
        }
        trackingStarted = true;

        // Web Gains
        (function (src) {
            var o = 'ITCLKQ';
            window[o] = window[o] || function () {
                (window[o].q = window[o].q || []).push(arguments)
            };
            window[o].l = 1 * new Date();
            var s = document.createElement('script');
            var f = document.getElementsByTagName('script')[0];
            s.async = 1;
            s.src = src;
            f.parentNode.insertBefore(s, f);
        })('https://analytics.webgains.io/11851/main.min.js');
        ITCLKQ('set', 'internal.api', true);
        ITCLKQ('set', 'internal.cookie', true);
        ITCLKQ('click');

        // Google Tag Manager
        (function (w, d, s, l, i) {
            w[l] = w[l] || [];
            w[l].push({
                'gtm.start':
                    new Date().getTime(), event: 'gtm.js'
            });
            var f = d.getElementsByTagName(s)[0],
                j = d.createElement(s), dl = l != 'dataLayer' ? '&l=' + l : '';
            j.async = true;
            j.src =
                'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
            f.parentNode.insertBefore(j, f);
        })(window, document, 'script', 'dataLayer', 'GTM-TKVFRL');
    }

    function customerPrivacy() {
        return window.Shopify && window.Shopify.customerPrivacy;
    }

    function trackingConsented() {
        const privacy = customerPrivacy();
        return Boolean(privacy && privacy.getTrackingConsent() === "yes");
    }

    function evaluateTracking() {
        if (trackingConsented()) {
            startTracking();
        }
    }

    function startWaiting() {
        if (waitingStarted) {
            return;
        }
        waitingStarted = true;

        // Consent decision arrives asynchronously (Consentmo -> Customer-Privacy-API)
        document.addEventListener("visitorConsentCollected", evaluateTracking);

        // Immediate evaluation: visitors who already consented have a stored decision
        evaluateTracking();

        // Fallback for the case where consent data is definitively not available:
        // no consent decision has arrived, so tracking is not started.
        setTimeout(() => {
            if (trackingStarted) {
                return;
            }
            if (trackingConsented()) {
                // A "yes" decision became available after the immediate
                // evaluation without a consent event (late API availability)
                startTracking();
                return;
            }
            const privacy = customerPrivacy();
            if (!privacy || privacy.getTrackingConsent() === "") {
                console.info(
                    "[consent-gate] tracking not started - consent data not available after "
                    + CONSENT_WAIT_TIMEOUT_MS + "ms"
                );
            }
        }, CONSENT_WAIT_TIMEOUT_MS);
    }

    // Run after the DOM is parsed, so the Customer-Privacy-API injected in
    // <head> is available for the immediate evaluation.
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", startWaiting);
    } else {
        startWaiting();
    }
})();
